/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Submit-pipeline contract gate (F15). Sits in front of every write
 * (`POST` / `PUT`) to `/api/story` and `/api/disclosure` and rejects
 * anything not shaped like an encrypted submission. Catches the
 * recurrent failure mode the redesign doc warns about — "sending
 * plaintext by accident in a debug path."
 *
 * The gate is a *belt*; the F13/F14 encryption path is the *suspenders*.
 * Both must agree, and the leak test in `tests/unit/api/leak.test.js`
 * independently verifies they do.
 *
 * Three rejection modes (with distinct `reason` codes):
 *
 *   'invalid-shape'     — Zod schema rejected the body (missing/extra fields)
 *   'too-short'         — `content` decoded to <40 bytes (24-byte nonce +
 *                          16-byte Poly1305 tag minimum)
 *   'plaintext-json'    — decoded `content` parsed as JSON
 *   'printable-ascii'   — ≥50% printable ASCII over the first 1 KiB of
 *                          decoded `content` (random ciphertext is ~37% printable)
 *
 * See `docs/Application_redesign.md` §3 stage 6 and
 * `docs/plan-crypto-redesign.md` §F15.
 */

import { z } from 'zod';
import { base64nopad } from '@scure/base';

/** Path prefixes the contract fires on. */
const CONTRACTED_PATHS = ['/api/story', '/api/disclosure'] as const;

/** HTTP methods that carry an encrypted submission body. */
const WRITE_METHODS = new Set(['POST', 'PUT']);

/** XChaCha20-Poly1305 nonce + min Poly1305 tag = floor on `content` length. */
const MIN_CONTENT_BYTES = 24 + 16;

/** Window over which we measure printable-ASCII rate. */
const ASCII_WINDOW_BYTES = 1024;

/** Threshold above which a decoded `content` blob looks like plaintext. */
const ASCII_PLAINTEXT_THRESHOLD = 0.5;

/* --- Errors --- */

export type PlaintextLeakReason =
  'invalid-shape' | 'too-short' | 'plaintext-json' | 'printable-ascii';

/**
 * Thrown when a contracted submission would have shipped plaintext or a
 * malformed shape. Never user-facing — the message identifies the
 * failure mode for engineers.
 */
export class PlaintextLeakError extends Error {
  readonly reason: PlaintextLeakReason;
  readonly path: string;
  constructor(message: string, reason: PlaintextLeakReason, path: string) {
    super(message);
    this.name = 'PlaintextLeakError';
    this.reason = reason;
    this.path = path;
  }
}

/* --- Schemas --- */

/**
 * Story submission body shape — matches `createStoryPayload` in F13.
 * Strict: unknown fields are rejected (catches stray debug fields).
 */
export const StorySubmissionSchema = z
  .object({
    content: z.string().min(1),
    checksum: z.string().min(1),
    documentId: z.string().min(1),
    version: z.number().int().min(0).max(0xffff),
    algorithmId: z.literal('XChaCha20-Poly1305'),
  })
  .strict();

export type StorySubmission = z.infer<typeof StorySubmissionSchema>;

/**
 * Disclosure-participant routing-sheet entry shape — matches the F14
 * routing-sheet entry. The server treats this as opaque; the contract
 * just enforces shape so a debug path can't ship a `key: null`.
 */
const DisclosureParticipantKeySchema = z
  .object({
    algorithmId: z.literal('ML-KEM-768+XChaCha20-Poly1305'),
    kemCiphertext: z.string().min(1),
    wrappedDk: z.string().min(1),
    nonce: z.string().min(1),
    recipientFingerprint: z.string().min(1),
    aad: z.string().min(1),
  })
  .strict();

const DisclosureParticipantSchema = z
  .object({
    identityId: z.string().min(1),
    key: DisclosureParticipantKeySchema,
  })
  .passthrough(); // notificationType, destination, status etc. allowed

/**
 * Disclosure submission body shape. The participants carry the F14
 * routing-sheet entries; surrounding metadata (whenType, checkInValue,
 * …) is permitted via passthrough on the outer envelope so legitimate
 * fields aren't blocked by the contract evolving slower than the form.
 *
 * #145: the field was `recipients` until 2026-05-12. Backend uses
 * `participants` (matching the `DisclosureParticipant` model name and
 * the broader domain language — these are participants in a
 * disclosure, not just recipients of an encrypted key). Backend
 * `@JsonIgnoreProperties(ignoreUnknown = true)` made the mismatch
 * silent — a body of `{ recipients: [...] }` deserialised to a
 * `Disclosure` with zero participants, dropping the routing sheet.
 * A transitional `@JsonAlias("recipients")` ships in
 * app-backend#39 to keep both names parsing during the rename window;
 * once this PR is in production, the alias gets removed there.
 */
export const DisclosureSubmissionSchema = z
  .object({
    participants: z.array(DisclosureParticipantSchema).min(1),
  })
  .passthrough();

export type DisclosureSubmission = z.infer<typeof DisclosureSubmissionSchema>;

/* --- Heuristics --- */

const isPrintableAscii = (b: number): boolean =>
  (b >= 0x20 && b <= 0x7e) || b === 0x09 || b === 0x0a || b === 0x0d;

/**
 * True if the decoded bytes look like plaintext under either heuristic
 * (JSON-parseable or ≥50% printable ASCII over the first 1 KiB). Used
 * by the contract gate to reject debug-path leaks.
 *
 * Pure function — exposed for tests.
 */
export const looksLikePlaintext = (
  bytes: Uint8Array
): { plaintext: true; reason: PlaintextLeakReason } | { plaintext: false } => {
  // JSON-parseable plaintext.
  if (bytes.length > 0 && bytes.length < 100 * 1024) {
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      JSON.parse(text);
      return { plaintext: true, reason: 'plaintext-json' };
    } catch {
      // Either not valid UTF-8 or not parseable JSON — fall through.
    }
  }

  // High printable-ASCII rate over a leading window.
  const window = Math.min(ASCII_WINDOW_BYTES, bytes.length);
  if (window > 0) {
    let printable = 0;
    for (let i = 0; i < window; i++) {
      if (isPrintableAscii(bytes[i] as number)) printable++;
    }
    if (printable / window >= ASCII_PLAINTEXT_THRESHOLD) {
      return { plaintext: true, reason: 'printable-ascii' };
    }
  }

  return { plaintext: false };
};

/* --- Contract enforcement --- */

const decodeContent = (content: string, path: string): Uint8Array => {
  try {
    return base64nopad.decode(content);
  } catch {
    throw new PlaintextLeakError(
      `content is not valid base64nopad`,
      'invalid-shape',
      path
    );
  }
};

const checkContentBlob = (content: string, path: string): void => {
  const decoded = decodeContent(content, path);
  if (decoded.length < MIN_CONTENT_BYTES) {
    throw new PlaintextLeakError(
      `content is ${decoded.length} bytes; envelopes are ≥ ${MIN_CONTENT_BYTES}`,
      'too-short',
      path
    );
  }
  const verdict = looksLikePlaintext(decoded);
  if (verdict.plaintext) {
    throw new PlaintextLeakError(
      `content failed plaintext heuristic (${verdict.reason})`,
      verdict.reason,
      path
    );
  }
};

/**
 * Normalise a `tdFetch`-style URL argument to its pathname so the
 * `path.startsWith('/api/...')` checks throughout this module are
 * URL-form-agnostic. F15's promise — "every write to `/api/story` or
 * `/api/disclosure` passes the contract gate" — must hold whether
 * the caller passes `'/api/story'`, `'https://api.example.com/api/story'`,
 * or `'https://example.com/api/story/123'`. The previous
 * `path.startsWith('/api/...')` check inside `isContractedPath`
 * rejected the absolute-URL form (it starts with `'https://'`),
 * silently skipping the gate (#112).
 *
 * Path-relative URLs are returned unchanged — the common production
 * case — so this is a cheap no-op for nearly every caller. Malformed
 * inputs (no scheme, no leading slash) fall back to the raw string;
 * the downstream `isContractedPath` check then rejects them safely.
 */
const toPath = (url: string): string => {
  if (url.startsWith('/')) return url;
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

/** True if `path` matches a contracted endpoint prefix. */
export const isContractedPath = (path: string): boolean =>
  CONTRACTED_PATHS.some(p => path.startsWith(p));

/** True if `method` is a write method that carries a submission body. */
export const isWriteMethod = (method: string): boolean =>
  WRITE_METHODS.has(method.toUpperCase());

/**
 * Validate a serialised submission body against the appropriate schema
 * and the plaintext heuristic. Throws `PlaintextLeakError` on any
 * failure; void on pass.
 *
 * @param path    request URL path (used to pick the schema branch and
 *                in error.path for diagnostics).
 * @param method  HTTP method (POST/PUT for contracted paths).
 * @param body    parsed body object — caller has already JSON.parse'd
 *                the wire form, so this is `unknown`.
 */
export const enforceSubmissionContract = (
  path: string,
  method: string,
  body: unknown
): void => {
  if (!isWriteMethod(method)) return;
  // #112: normalise so absolute URLs (`https://host/api/story`) hit
  // the same path-prefix check as relative ones. Reassign `path` so
  // downstream `startsWith` branches and `PlaintextLeakError.path`
  // diagnostics see the pathname, not the full URL.
  path = toPath(path);
  if (!isContractedPath(path)) return;

  if (path.startsWith('/api/story')) {
    const result = StorySubmissionSchema.safeParse(body);
    if (!result.success) {
      throw new PlaintextLeakError(
        `story submission shape rejected: ${result.error.message}`,
        'invalid-shape',
        path
      );
    }
    checkContentBlob(result.data.content, path);
    return;
  }

  if (path.startsWith('/api/disclosure')) {
    const result = DisclosureSubmissionSchema.safeParse(body);
    if (!result.success) {
      throw new PlaintextLeakError(
        `disclosure submission shape rejected: ${result.error.message}`,
        'invalid-shape',
        path
      );
    }
    // Per-participant key blob — sanity-check each wrappedDk.
    for (const participant of result.data.participants) {
      checkContentBlob(participant.key.wrappedDk, path);
    }
    return;
  }
};

/**
 * Convenience wrapper for the tdFetch gate. Parses a JSON-or-already-parsed
 * body, then runs `enforceSubmissionContract`. Throws `PlaintextLeakError`
 * on non-JSON or contract failure.
 */
export const enforceFromRequest = (
  path: string,
  method: string,
  body: BodyInit | null | undefined
): void => {
  if (!isWriteMethod(method)) return;
  // #112: normalise so absolute URLs are checked against the same
  // path prefixes as relative ones. Reassigning `path` keeps every
  // downstream branch + diagnostic on the canonical pathname.
  path = toPath(path);
  if (!isContractedPath(path)) return;
  if (body === null || body === undefined) {
    throw new PlaintextLeakError(
      `${method} ${path} requires a body`,
      'invalid-shape',
      path
    );
  }
  if (typeof body !== 'string') {
    throw new PlaintextLeakError(
      `${method} ${path} body must be a JSON string`,
      'invalid-shape',
      path
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    throw new PlaintextLeakError(
      `${method} ${path} body is not valid JSON`,
      'invalid-shape',
      path
    );
  }
  enforceSubmissionContract(path, method, parsed);
};
